import Seo from "../components/Seo";
import Header from "../components/tscm/Header";
import Footer from "../components/tscm/Footer";
import Contact from "../components/tscm/Contact";
import { scrollToId } from "../lib/scroll";
import { PHONE_DISPLAY, PHONE_TEL } from "../constants/site";
import {
  Breadcrumb,
  BeforeYouCall,
  ProgrammeLinks,
  SubpageHero,
  Section,
  CoverList,
  NumberedList,
  StepList,
} from "./SubpageBlocks";

const LD = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Preventive TSCM",
    description:
      "Preventive TSCM: a boardroom or meeting space inspected close to the meeting, with a written report. Corporate TSCM across India by Law Abiding Technology.",
    areaServed: "India",
    provider: { "@id": "https://tscmservices.in/#org" },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://tscmservices.in/" },
      { "@type": "ListItem", position: 2, name: "Preventive TSCM", item: "https://tscmservices.in/preventive-tscm" },
    ],
  },
];

export default function PreventiveTscm() {
  return (
    <div id="top" className="min-h-screen bg-base-color font-body text-ink">
      <Seo
        title="Preventive TSCM — Sweeps Before the Meeting | TSCM Services"
        description="Preventive TSCM: a boardroom or meeting space inspected close to the meeting, with a written report. Corporate TSCM across India by Law Abiding Technology."
        path="/preventive-tscm"
        jsonLd={LD}
      />
      <Header />
      <main>
        <div className="site-x pt-6">
          <Breadcrumb current="Preventive TSCM" />
        </div>
        <SubpageHero
          tag="Preventive TSCM · Before the meeting"
          lines={["The room checked close", "to the meeting, not", "weeks ahead."]}
          image="/media/boardroom-team-sweep-wide.jpg"
          imageAlt="A three-person team sweeping a boardroom — antenna work, spectrum analysis and a physical search running in parallel"
        >
          <p>
            A preventive TSCM inspection (a bug sweep) examines a boardroom or
            meeting space before a sensitive meeting, set against your
            calendar rather than long before it. A sensitive discussion can be
            exposed through the room it takes place in; checking the room
            close to the meeting reduces that exposure. It confirms the space
            at that point in time.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <button
              onClick={() => scrollToId("contact")}
              data-testid="subpage-request-button"
              className="rounded-full bg-violet px-6 py-3 text-[0.9375rem] font-semibold text-[#1a1021] transition-colors hover:bg-violeth"
            >
              Request a confidential consultation
            </button>
            <a
              href={PHONE_TEL}
              data-testid="subpage-phone-link"
              className="text-sm text-sub underline decoration-hairline underline-offset-4 transition-colors hover:text-ink"
            >
              or call {PHONE_DISPLAY}
            </a>
          </div>
        </SubpageHero>

        <Section title="What it covers." wide>
          <CoverList
            items={[
              "Board and audit committee sittings",
              "Transaction and investor discussions",
              "Hotel and conference venues",
              "Rented or temporary offices",
            ]}
          />
        </Section>

        <Section title="Set against the calendar.">
          <NumberedList
            items={[
              "Before every board or audit committee sitting",
              "Ahead of fundraise, M&A or investor discussions",
              "Before a hotel or hired venue hosts your discussion",
              "After contractors, maintenance or events have been in the room",
            ]}
          />
          <p className="mt-8 text-base leading-relaxed text-sub">
            An inspection confirms the room at the time of attendance. From
            the moment the team leaves, access resumes, so the room inspected
            on Monday is not necessarily the room you walk into on Thursday.
          </p>
        </Section>

        <Section title="How an engagement runs." wide>
          <StepList
            steps={[
              { bold: "A scoping call", rest: ": the room, the venue, and the time we can attend." },
              { bold: "The inspection, scheduled close to the meeting", rest: ", at a time set by you." },
              { bold: "A written record", rest: ": findings sealed and delivered to the person who engaged us." },
              { bold: "If you want a programme", rest: ": scheduled inspections through the year under an annual contract." },
            ]}
          />
        </Section>

        <Section title="What it does not do.">
          <p className="text-base leading-relaxed text-sub">
            It confirms the room at the time of attendance. It does not
            guarantee the room afterwards, and what enters the room after the
            team leaves is a separate question.
          </p>
        </Section>

        <BeforeYouCall />
        <ProgrammeLinks
          links={[
            { href: "/corporate-tscm", tag: "Reactive", title: "Corporate TSCM inspection" },
            { href: "/live-meeting-monitoring", tag: "During the meeting", title: "Live meeting monitoring" },
            { href: "/#contact", tag: "Ongoing", title: "Ask about an annual contract" },
          ]}
        />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
